import { useParams, Link } from 'react-router-dom';
import { useProjectDetail, useProjectPosts } from '../hooks/use-api';
import { Spinner } from '../components/shared/Spinner';
import { EmptyState } from '../components/shared/EmptyState';
import { LoadMoreButton } from '../components/shared/LoadMoreButton';
import { StatGrid } from '../components/shared/StatGrid';
import { InfoBlock } from '../components/shared/InfoBlock';
import { formatRelativeTime } from '../hooks/use-relative-time';
import styles from './ProjectDetailPage.module.css';

const ProjectDetailPage = (): React.ReactElement => {
  const { projectId } = useParams();
  const safeId = projectId ?? '';

  const detailQuery = useProjectDetail(safeId);
  const { items: posts, isLoading: postsLoading, hasMore: postsHasMore, loadMore: postsLoadMore, isFetchingNextPage: postsFetchingNext } = useProjectPosts(safeId);

  if (detailQuery.isLoading) {
    return (
      <div className={styles.page}>
        <Spinner />
      </div>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className={styles.page}>
        <EmptyState message="Project not found" />
      </div>
    );
  }

  const project = detailQuery.data;

  return (
    <div className={styles.page}>
      <button data-testid="back" className={styles.backButton} onClick={() => { window.history.back(); }} type="button">
        &larr; Back
      </button>

      <header className={styles.header}>
        <span className={styles.categoryBadge}>{project.category}</span>
        <h1 className={styles.projectName}>{project.name}</h1>
        <p className={styles.description}>{project.description}</p>
      </header>

      <InfoBlock title="Metrics">
        <StatGrid items={[
          { label: 'Region', value: project.category, color: 'default', onClick: null },
          { label: 'Status', value: project.status, color: project.status === 'active' ? 'accent' : 'default', onClick: null },
          { label: 'Forecasts', value: project.forecasts, color: 'accent', onClick: null },
          { label: 'Verifications', value: project.verifications, color: 'default', onClick: null },
        ]} />
      </InfoBlock>

      <InfoBlock title="Discussion">
        {postsLoading ? (
          <Spinner />
        ) : posts.length === 0 ? (
          <EmptyState message="No discussion yet" />
        ) : (
          <div className={styles.postList}>
            {posts.map((post, idx) => (
              <div key={idx} className={styles.post}>
                <div className={styles.postHeader}>
                  <Link to={`/entity/${post.entityId}`} className={styles.entityLink}>{post.entityName}</Link>
                  <span className={styles.postTime}>{formatRelativeTime(post.createdAt)}</span>
                </div>
                <div className={styles.postContent}>{post.content}</div>
              </div>
            ))}
            <LoadMoreButton onClick={postsLoadMore} isLoading={postsFetchingNext} hasMore={postsHasMore} />
          </div>
        )}
      </InfoBlock>
    </div>
  );
};

export { ProjectDetailPage };
